import { useCallback, useEffect, useState } from 'react'
import { useEventCallback } from 'studiokit-scaffolding-js/lib/hooks/useEventCallback'
import { useFetchData } from 'studiokit-scaffolding-js/lib/hooks/useFetchData'
import { FETCH_STATUS } from '../types/FetchStatus'
import { TokenUsage } from '../types/TokenUsage'

/**
 * How often to re-check usage. The API's window slides, so usage drains on its own — polling is what
 * releases a blocked user, and keeps the meter honest, without a reload.
 */
export const USAGE_POLL_INTERVAL_MS = 1000 * 60

/**
 * Loads and periodically refreshes the current user's token usage.
 *
 * @param isEnabled Whether usage applies, i.e. there is an authenticated user who is allowed to spend tokens.
 * @returns The latest usage, and a method to re-check it after something has spent tokens.
 */
export function useTokenUsage(isEnabled: boolean) {
	const [usage, setUsage] = useState<TokenUsage | undefined>()

	const { status, send: getUsage } = useFetchData(
		'usage',
		(data: unknown) => {
			setUsage(data as TokenUsage)
		},
		() => {
			// keep the last known usage. A failed check is not evidence the user has capacity again,
			// and blanking the meter on a blip is worse than showing a slightly stale percentage.
		}
	)

	// `send` throws while a request is in progress, and the poll, the initial load and `refreshUsage`
	// can all land on top of each other
	const getUsageIfNotFetching = useEventCallback(() => {
		if (status !== FETCH_STATUS.FETCHING) {
			getUsage()
		}
	})
	const refreshUsage = useCallback(() => {
		getUsageIfNotFetching()
	}, [getUsageIfNotFetching])

	const onIsEnabledChange = useEventCallback(() => {
		if (isEnabled) {
			getUsageIfNotFetching()
		} else {
			setUsage(undefined)
		}
	})
	useEffect(() => {
		onIsEnabledChange()
	}, [isEnabled, onIsEnabledChange])

	useEffect(() => {
		if (!isEnabled) {
			return
		}
		const intervalId = window.setInterval(() => {
			getUsageIfNotFetching()
		}, USAGE_POLL_INTERVAL_MS)
		return () => {
			window.clearInterval(intervalId)
		}
	}, [isEnabled, getUsageIfNotFetching])

	return { usage, refreshUsage }
}
